import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';
import feedbackAnalyticsService from '../../../../services/feedback-analytics';
import type { FeedbackFormOption } from '../../../../services/feedback-analytics';

interface FiltersData {
  dateFrom?: string;
  dateTo?: string;
  feedBackFormId?: number;
}

interface FiltersProps {
  onFilterChange: (filters: FiltersData) => void;
  loading?: boolean;
}

export const FeedbackFilters: React.FC<FiltersProps> = ({
  onFilterChange,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formId, setFormId] = useState('');
  const [forms, setForms] = useState<FeedbackFormOption[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState<string | null>(null);

  useEffect(() => {
    const loadForms = async () => {
      setFormsLoading(true);
      setFormsError(null);

      try {
        const response = await feedbackAnalyticsService.getFeedbackForms();
        setForms(response);
      } catch {
        setFormsError('Nao foi possivel carregar os formularios.');
      } finally {
        setFormsLoading(false);
      }
    };

    loadForms();
  }, []);

  const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const filters: FiltersData = {};

    if (dateFrom) {
      filters.dateFrom = new Date(dateFrom).toISOString();
    }

    if (dateTo) {
      filters.dateTo = new Date(dateTo).toISOString();
    }

    if (formId) {
      filters.feedBackFormId = Number.parseInt(formId);
    }

    onFilterChange(filters);
  };

  const handleClear = () => {
    setDateFrom('');
    setDateTo('');
    setFormId('');
    onFilterChange({});
  };

  return (
    <Card className="mb-5 border-0 shadow-sm">
      <Card.Header 
        className="bg-white border-bottom"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            🔍 Filtros
          </h5>
          <span className="text-muted">
            {isOpen ? '▼' : '▶'}
          </span>
        </div>
      </Card.Header>

      {isOpen && (
        <Card.Body>
          <Form onSubmit={handleFilterSubmit}>
            <Row className="g-3">
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Data Inicial</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Data Final</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Formulário</Form.Label>
                  <Form.Select
                    value={formId}
                    onChange={(e) => setFormId(e.target.value)}
                    disabled={loading || formsLoading}
                  >
                    <option value="">Todos os formulários</option>
                    {forms.map((form) => (
                      <option key={form.id} value={form.id}>
                        {form.title}
                      </option>
                    ))}
                  </Form.Select>
                  {formsError && (
                    <small className="text-danger d-block mt-2">{formsError}</small>
                  )}
                </Form.Group>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>&nbsp;</Form.Label>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading}
                      className="flex-grow-1"
                    >
                      Aplicar Filtros
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleClear}
                      type="button"
                    >
                      Limpar
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      )}
    </Card>
  );
};

export default FeedbackFilters;
